import { OptionData } from '@/components/ui/combo';
import api from '@/services/api/api';
import PaginationData from '@/types/Api/PaginationData';
import { ResponsePaginationData } from '@/types/Api/ResponsePaginationData';
import { Converter } from '@/utils/Converter';
import { DefinedInitialDataInfiniteOptions, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useAbility } from './ability';
import { PaginationState } from './use-pagination';

export type SelectInfiniteQueryParser<T = unknown> = (data: T[]) => OptionData<T>[];
export type SelectInfiniteQueryParams = Partial<PaginationState> & { [key: string]: any };

export const useSelectInfiniteQuery = <T, B>(
  {
    permissions,
    url,
    params,
    ...queryOptions
  }: Omit<
    DefinedInitialDataInfiniteOptions<PaginationData<OptionData<T>>>,
    'getNextPageParam' | 'initialPageParam' | 'initialData'
  > & {
    permissions?: string[] | string;
    url: string;
    params?: SelectInfiniteQueryParams;
  },
  parseSelectOptions?: SelectInfiniteQueryParser<B>,
) => {
  const ability = useAbility();

  return useInfiniteQuery<PaginationData<OptionData<T>>>({
    ...queryOptions,
    placeholderData: keepPreviousData,
    queryKey: [...queryOptions.queryKey, `search=${params?.search || ''}`, `active=${params?.active || ''}`],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }: { pageParam?: PaginationData<any>['current'] }) => {
      if (permissions && permissions.length) {
        if (Array.isArray(permissions)) {
          if (!permissions.some(permission => ability.can(permission, '')))
            return {
              data: [],
              current_page: 1,
              total: 0,
            };
        } else {
          if (!ability.can(permissions, ''))
            return {
              data: [],
              current_page: 1,
              total: 0,
            };
        }
      }

      delete params?.pageSize;
      delete params?.current;
      delete params?.total;
      delete params?.lastPage;
      delete params?.dispatchPagination;
      const filters = params?.filter;
      delete params?.filter;
      const response = await api.get<ResponsePaginationData<T>>(url, {
        params: {
          ...(params || {}),
          ...(filters || {}),
          page: pageParam.toString(),
          search: params?.search,
          active: params?.active,
          per_page: params?.pageSize || 10,
        },
      });

      let data: PaginationData<T>;
      if ('message' in response.data) data = response.data.data as PaginationData<T>;
      else data = response.data;

      const parseResponseData = (): OptionData<T>[] => {
        if (parseSelectOptions) return parseSelectOptions(data.data);
        return Converter.selectOptionArray(data.data);
      };

      return {
        ...data,
        data: parseResponseData(),
      };
    },
    getNextPageParam: lastPage =>
      lastPage?.current_page < (lastPage?.last_page ?? 1) ? lastPage?.current_page + 1 : undefined,
    getPreviousPageParam: lastPage => (lastPage?.current_page > 0 ? lastPage?.current_page - 1 : undefined),
  });
};
